// ✅ Updated ArtistProfile.js with 5MB image size check and document ID = UID logic
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db, auth, storage } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import CustomModal from "../components/CustomModal";
import AvatarSelector, { AVATAR_OPTIONS, getAvatarEmoji } from "../components/AvatarSelector";
import { motion } from "framer-motion";
import { Home } from "lucide-react";
import {
  onboardingInputClass,
  profileStepCardClass,
  profileStyleChipActive,
  profileStyleChipIdle,
  uploadWizardLabelClass,
  uploadWizardPanelClass,
  uploadWizardStepTitleClass,
} from "../components/OnboardingModalShell";
import { artNavPillBtn, artPrimaryBtnLg, artSecondaryBtnLg } from "../ui/artmosphereButtonTheme";
import { invalidateArtistProfileCache } from "../utils/artistProfileCache";

const PROFILE_STEPS = [
  { id: "photo", label: "Photo" },
  { id: "about", label: "About" },
  { id: "dreams", label: "Creative" },
  { id: "styles", label: "Styles" },
];

const profileStepIndex = (sectionId) => PROFILE_STEPS.findIndex((s) => s.id === sectionId);

const MAX_PROFILE_IMAGE_BYTES = 5 * 1024 * 1024;

function isPersistentImageUrl(url) {
  return typeof url === "string" && /^https?:\/\//i.test(url);
}

function dataURLToFile(dataUrl, filename = "profile.jpg") {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
  const bin = atob(base64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

async function uploadProfilePictureToStorage(user, file) {
  const safeName = (file.name || "profile.jpg").replace(/[^\w.-]/g, "_");
  const imgRef = ref(storage, `profiles/${user.uid}/${Date.now()}-${safeName}`);
  await uploadBytes(imgRef, file, {
    contentType: file.type || "image/jpeg",
  });
  return getDownloadURL(imgRef);
}

export default function ArtistProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [image, setImage] = useState(null);
  const [imageURL, setImageURL] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null); // For under-13 users
  const [isUnder13, setIsUnder13] = useState(false);
  const [name, setName] = useState("");
  const [nickname, setNickname] = useState("");
  const [birthday, setBirthday] = useState("");
  const [dobPickerOpen, setDobPickerOpen] = useState(false);
  const [gender, setGender] = useState("");
  const [bio, setBio] = useState("");
  const [styles, setStyles] = useState([]);
  const [artistType, setArtistType] = useState("");
  const [favoriteColor, setFavoriteColor] = useState("");
  const [dreamArtPlace, setDreamArtPlace] = useState("");
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    confirmText: "OK",
  });
  const [openSection, setOpenSection] = useState("photo");
  const [hasExistingProfile, setHasExistingProfile] = useState(false);
  const [profilePhotoRemoved, setProfilePhotoRemoved] = useState(false);

  const availableStyles = [
    "Impressionism",
    "Cubism",
    "Pop Art",
    "Abstract",
    "Realism",
    "Surrealism",
  ];

  // If user landed here right after a successful payment,
  // show a premium welcome popup to create their artist profile.
  const paymentWelcomeShownRef = useRef(false);
  const redirectAfterSaveRef = useRef(false);
  const redirectTimerRef = useRef(null);

  const closeModal = () => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    const shouldRedirect = redirectAfterSaveRef.current;
    redirectAfterSaveRef.current = false;
    setModal({ isOpen: false, title: "", message: "", type: "info", confirmText: "OK" });
    if (shouldRedirect) navigate("/my-art-world", { state: { profileUpdated: true } });
  };

  const showProfileSavedSuccess = (isEdit) => {
    redirectAfterSaveRef.current = true;
    setModal({
      isOpen: true,
      title: isEdit ? "Changes saved" : "Profile saved!",
      message: isEdit
        ? "Your artist profile has been updated. Taking you to Artist Corner..."
        : "Welcome to Artmosphere! Your profile is ready — taking you to Artist Corner.",
      type: "success",
      confirmText: "Go to Artist Corner",
    });
    redirectTimerRef.current = setTimeout(() => {
      redirectTimerRef.current = null;
      if (redirectAfterSaveRef.current) closeModal();
    }, 2500);
  };
  useEffect(() => {
    const paymentSuccess = location.state?.paymentSuccess === true;
    if (!paymentSuccess) return;
    if (paymentWelcomeShownRef.current) return;
    paymentWelcomeShownRef.current = true;

    setModal({
      isOpen: true,
      title: "Welcome to Artmosphere Studio!",
      message: "Let’s get your artist profile created.",
      type: "success",
    });
  }, [location.state?.paymentSuccess]);

  useEffect(() => {
    const loadProfile = async () => {
      const user = auth.currentUser;
      if (!user) return;
      
      // Check if user is under 13
      let under13 = false;
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          under13 = !!userData.isUnder13;
          setIsUnder13(under13);
        }
      } catch (e) {
        console.error('Error checking user age:', e);
      }
      
      const docRef = doc(db, "artistProfiles", user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setHasExistingProfile(true);
        const data = docSnap.data();
        setName(data.name || "");
        setNickname(data.nickname || "");
        setBirthday(data.birthday || "");
        setGender(data.gender || "");
        setBio(data.bio || "");
        setStyles(data.styles || []);
        setArtistType(data.artistType || "");
        // For under-13: use avatar, for others: use profile picture
        if (under13) {
          setSelectedAvatar(data.avatarId || null);
        } else {
          setImageURL(data.profilePicture || "");
        }
        setFavoriteColor(data.favoriteColor || "");
        setDreamArtPlace(data.dreamArtPlace || "");
      }
    };

    loadProfile();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setModal({
        isOpen: true,
        title: "Image Too Large",
        message: "Please upload a smaller image (under 5MB) so we can save your beautiful profile picture!",
        type: 'warning'
      });
      return;
    }
    setProfilePhotoRemoved(false);
    setImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setImageURL(reader.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImage(null);
    setImageURL(null);
    setProfilePhotoRemoved(true);
  };

  const toggleStyle = (style) => {
    setStyles((prev) =>
      prev.includes(style) ? prev.filter((s) => s !== style) : [...prev, style]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !birthday || !gender) {
      setOpenSection("about");
      setModal({
        isOpen: true,
        title: "Missing Information",
        message: "Please fill in all the required fields (Name, Birthday, and Gender) to create your amazing artist profile!",
        type: 'warning'
      });
      return;
    }

    // For under-13 users, require avatar selection
    if (isUnder13 && !selectedAvatar) {
      setOpenSection("photo");
      setModal({
        isOpen: true,
        title: "Avatar Required",
        message: "Please select an avatar for your profile!",
        type: 'warning'
      });
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setModal({
        isOpen: true,
        title: "Session Needed",
        message: "Please sign in again, then save your artist profile.",
        type: "warning"
      });
      return;
    }

    try {
      let profilePictureUrl = null;

      if (!isUnder13) {
        if (profilePhotoRemoved) {
          profilePictureUrl = null;
        } else {
          let fileToUpload = image;

          if (!fileToUpload && typeof imageURL === "string" && imageURL.startsWith("data:")) {
            fileToUpload = dataURLToFile(imageURL);
          }

          if (fileToUpload) {
            if (fileToUpload.size > MAX_PROFILE_IMAGE_BYTES) {
              setOpenSection("photo");
              setModal({
                isOpen: true,
                title: "Image Too Large",
                message:
                  "Please upload a smaller image (under 5MB) so we can save your beautiful profile picture!",
                type: "warning",
              });
              return;
            }
            try {
              profilePictureUrl = await uploadProfilePictureToStorage(user, fileToUpload);
            } catch (storageError) {
              console.error("Profile photo upload failed:", storageError);
              setOpenSection("photo");
              setModal({
                isOpen: true,
                title: "Photo Upload Failed",
                message:
                  "We couldn't upload your profile photo. Please check your connection and try again. " +
                  "Your other profile details were not saved yet.",
                type: "warning",
              });
              return;
            }
          } else if (isPersistentImageUrl(imageURL)) {
            profilePictureUrl = imageURL;
          }
        }
      }

      const docRef = doc(db, "artistProfiles", user.uid);
      const profileData = {
        uid: user.uid,
        name,
        nickname: nickname || name, // Use name as default if nickname not provided
        birthday,
        gender,
        bio,
        styles,
        artistType,
        favoriteColor,
        dreamArtPlace,
      };

      // For under-13: save avatarId, for others: save profilePicture (never a data: URL)
      if (isUnder13) {
        profileData.avatarId = selectedAvatar;
      } else if (profilePhotoRemoved) {
        profileData.profilePicture = null;
      } else if (profilePictureUrl) {
        profileData.profilePicture = profilePictureUrl;
      }

      await setDoc(docRef, profileData, { merge: true });
      invalidateArtistProfileCache(user.uid);

      if (!isUnder13 && (profilePictureUrl || profilePhotoRemoved)) {
        await setDoc(
          doc(db, "users", user.uid),
          { profilePicture: profilePhotoRemoved ? null : profilePictureUrl },
          { merge: true }
        );
      }

      if (!isUnder13) {
        if (profilePhotoRemoved) {
          setImageURL(null);
          setImage(null);
          setProfilePhotoRemoved(false);
        } else if (profilePictureUrl) {
          setImageURL(profilePictureUrl);
          setImage(null);
        }
      }

      const wasEdit = hasExistingProfile;
      setHasExistingProfile(true);
      showProfileSavedSuccess(wasEdit);
    } catch (error) {
      console.error("Failed to save artist profile:", error);
      setModal({
        isOpen: true,
        title: "Could Not Save Profile",
        message: "Something went wrong while saving your profile. Please try again.",
        type: "warning"
      });
    }
  };

  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.size > 5 * 1024 * 1024) {
        setModal({
          isOpen: true,
          title: "Image Too Large",
          message: "Please upload a smaller image (under 5MB) so we can save your beautiful profile picture!",
          type: 'warning'
        });
        return;
      }
      setProfilePhotoRemoved(false);
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImageURL(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const goHome = () => {
    navigate("/home");
  };

  const toggleSection = (sectionKey) => {
    setOpenSection((prev) => (prev === sectionKey ? "" : sectionKey));
  };

  const formatDobDisplay = (dateValue) => {
    if (!dateValue) return "Select your date of birth";
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return dateValue;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const handleDobSelect = (selectedDate) => {
    if (!(selectedDate instanceof Date) || Number.isNaN(selectedDate.getTime())) return;
    const yyyy = selectedDate.getFullYear();
    const mm = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const dd = String(selectedDate.getDate()).padStart(2, "0");
    setBirthday(`${yyyy}-${mm}-${dd}`);
    setDobPickerOpen(false);
  };

  const activeStepIdx = openSection ? profileStepIndex(openSection) : 0;

  const sectionToggleBtn = (sectionId) => (
    <button
      type="button"
      onClick={() => toggleSection(sectionId)}
      className="flex w-full items-center justify-between gap-3 text-left"
      aria-expanded={openSection === sectionId}
    >
      <h2 className={uploadWizardStepTitleClass}>
        {sectionId === "photo" && (isUnder13 ? "Choose your avatar" : "Your artist photo")}
        {sectionId === "about" && "Tell us about yourself"}
        {sectionId === "dreams" && "Your creative dreams"}
        {sectionId === "styles" && "Favorite art styles"}
      </h2>
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#F5D99B] bg-[#FFF9EF] text-sm font-semibold text-[#8A7357]"
        aria-hidden
      >
        {openSection === sectionId ? "−" : "+"}
      </span>
    </button>
  );

  return (
    <div className="auth-premium-page">
      <div className="auth-premium-shell max-w-3xl">
        <div className="auth-premium-gold-rule" aria-hidden />
        <div className="px-4 py-5 sm:px-8 sm:py-8">
          <div className="mb-4 flex justify-end">
            <button type="button" onClick={goHome} className={artNavPillBtn}>
              <Home className="h-4 w-4 shrink-0" aria-hidden />
              <span>Home</span>
            </button>
          </div>

          <motion.header
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-5 text-center sm:mb-6"
          >
            <p className="auth-premium-eyebrow mb-2">Artist profile</p>
            <h1 className="auth-premium-title text-2xl sm:text-3xl md:text-4xl">
              {hasExistingProfile ? "Edit your profile" : "Welcome, future artist"}
            </h1>
            <p className="auth-premium-subtitle mx-auto mt-2 max-w-md text-sm sm:text-base">
              {hasExistingProfile
                ? "Update your details anytime — tap Save when you are done."
                : "A few steps to set up your studio identity — photo, about you, and the styles you love."}
            </p>
          </motion.header>

          {!hasExistingProfile && (
            <nav className="signup-progress" aria-label="Profile setup progress">
              <div className="signup-progress__row">
                {PROFILE_STEPS.map((step, i) => {
                  const isActive = i <= activeStepIdx;
                  const isCurrent = step.id === openSection;
                  return (
                    <React.Fragment key={step.id}>
                      <div className="signup-progress__step">
                        <div
                          className={`signup-progress__dot ${isActive ? "signup-progress__dot--active" : ""}`}
                          aria-current={isCurrent ? "step" : undefined}
                        >
                          {i + 1}
                        </div>
                        <span
                          className={`signup-progress__label ${isActive ? "signup-progress__label--active" : ""}`}
                        >
                          {step.label}
                        </span>
                      </div>
                      {i < PROFILE_STEPS.length - 1 && (
                        <div className="signup-progress__connector" aria-hidden>
                          <div
                            className={`signup-progress__connector-fill ${
                              i < activeStepIdx ? "signup-progress__connector-fill--done" : ""
                            }`}
                          />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </nav>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <motion.section
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05 }}
              className={`${profileStepCardClass} overflow-visible`}
            >
              {sectionToggleBtn("photo")}
              {openSection === "photo" && (
                <div className="mt-4">
                  {isUnder13 ? (
                    <div className="flex justify-center">
                      <AvatarSelector
                        selectedAvatar={selectedAvatar}
                        onSelectAvatar={setSelectedAvatar}
                      />
                    </div>
                  ) : (
                    <div className={`${uploadWizardPanelClass} flex flex-col items-center gap-4 py-6`}>
                      <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        className={`relative h-32 w-32 overflow-hidden rounded-full border-2 transition-all duration-300 sm:h-40 sm:w-40 ${
                          dragActive
                            ? "scale-105 border-[#F59E0B] shadow-[0_12px_32px_rgba(234,88,12,0.2)]"
                            : imageURL
                              ? "border-[#F5CB5C] shadow-[0_8px_24px_rgba(120,72,20,0.12)]"
                              : "border-dashed border-[#F5D99B] bg-[#FFFDF9]"
                        }`}
                      >
                        {imageURL ? (
                          <motion.img
                            initial={{ scale: 0.92 }}
                            animate={{ scale: 1 }}
                            src={imageURL}
                            alt="Profile"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full flex-col items-center justify-center text-center text-xs font-medium text-[#8A7357] sm:text-sm">
                            <span>Add photo</span>
                            <span className="mt-0.5 text-[10px] font-normal text-[#9A7A55] sm:text-xs">
                              or drag here
                            </span>
                          </div>
                        )}
                        {dragActive && (
                          <div className="absolute inset-0 flex items-center justify-center bg-[#F59E0B]/25 text-sm font-semibold text-[#2F251B]">
                            Drop here
                          </div>
                        )}
                      </div>
                      <label className="block">
                        <input
                          type="file"
                          onChange={handleImageChange}
                          className="hidden"
                          accept="image/*"
                          id="profile-picture"
                        />
                        <span className={`${artPrimaryBtnLg} cursor-pointer`}>Choose photo</span>
                      </label>
                      {imageURL && (
                        <button
                          type="button"
                          onClick={removeImage}
                          className={`${artSecondaryBtnLg} !min-h-0 !px-4 !py-2 text-xs`}
                        >
                          Remove photo
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1 }}
              className={`${profileStepCardClass} overflow-visible ${dobPickerOpen ? "relative z-[220]" : ""}`}
            >
              {sectionToggleBtn("about")}
              {openSection === "about" && (
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
                  <div>
                    <label className={uploadWizardLabelClass}>
                      Full name <span className="text-[#C2410C]">*</span>
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={onboardingInputClass}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className={uploadWizardLabelClass}>
                      Nickname{" "}
                      <span className="text-xs font-normal text-[#9A7A55]">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={nickname}
                      onChange={(e) => setNickname(e.target.value)}
                      className={onboardingInputClass}
                      placeholder="Artist name"
                    />
                  </div>
                  <div className="relative sm:col-span-1">
                    <label className={uploadWizardLabelClass}>
                      Birthday <span className="text-[#C2410C]">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setDobPickerOpen((open) => !open)}
                      className={`${onboardingInputClass} flex items-center justify-between text-left`}
                      aria-label="Select birthday"
                    >
                      <span className={birthday ? "text-[#2F251B]" : "text-[#9A7A55]"}>
                        {formatDobDisplay(birthday)}
                      </span>
                      <span className="text-sm text-[#8A7357]" aria-hidden>
                        📅
                      </span>
                    </button>
                    <input type="hidden" value={birthday} readOnly />
                    {dobPickerOpen && (
                      <div className="absolute z-[200] mt-2 w-full min-w-[16rem] max-w-xs rounded-[16px] border border-[#F5D99B] bg-[#FFFDF9] p-3 shadow-[0_12px_40px_rgba(120,72,20,0.12)]">
                        <DobCalendar
                          value={birthday}
                          onSelect={handleDobSelect}
                          onClose={() => setDobPickerOpen(false)}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className={uploadWizardLabelClass}>
                      Gender <span className="text-[#C2410C]">*</span>
                    </label>
                    <select
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      className={onboardingInputClass}
                    >
                      <option value="">Choose…</option>
                      <option>Girl</option>
                      <option>Boy</option>
                      <option>Non-binary</option>
                      <option>Prefer not to say</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={uploadWizardLabelClass}>How do you see yourself?</label>
                    <select
                      value={artistType}
                      onChange={(e) => setArtistType(e.target.value)}
                      className={onboardingInputClass}
                    >
                      <option value="">Choose your artist type…</option>
                      <option>Newbie Artist</option>
                      <option>Professional Artist</option>
                      <option>Learning by Doing</option>
                      <option>Art Lover</option>
                    </select>
                  </div>
                </div>
              )}
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.15 }}
              className={profileStepCardClass}
            >
              {sectionToggleBtn("dreams")}
              {openSection === "dreams" && (
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
                    <div>
                      <label className={uploadWizardLabelClass}>Favorite color</label>
                      <input
                        type="text"
                        value={favoriteColor}
                        onChange={(e) => setFavoriteColor(e.target.value)}
                        className={onboardingInputClass}
                        placeholder="E.g. sky blue"
                      />
                    </div>
                    <div>
                      <label className={uploadWizardLabelClass}>Dream place to paint</label>
                      <input
                        type="text"
                        value={dreamArtPlace}
                        onChange={(e) => setDreamArtPlace(e.target.value)}
                        className={onboardingInputClass}
                        placeholder="E.g. a mountain studio"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={uploadWizardLabelClass}>Your bio</label>
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      className={`${onboardingInputClass} resize-none`}
                      rows={4}
                      placeholder="Tell us about yourself and your love for art…"
                    />
                  </div>
                </div>
              )}
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.2 }}
              className={profileStepCardClass}
            >
              {sectionToggleBtn("styles")}
              {openSection === "styles" && (
                <div className="mt-4">
                  <p className="mb-3 text-sm text-[#6B5A48]">
                    Tap the styles you love — choose as many as you like.
                  </p>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {availableStyles.map((style) => (
                      <button
                        key={style}
                        type="button"
                        onClick={() => toggleStyle(style)}
                        className={
                          styles.includes(style) ? profileStyleChipActive : profileStyleChipIdle
                        }
                      >
                        {styles.includes(style) && <span className="mr-1.5">✓</span>}
                        {style}
                      </button>
                    ))}
                  </div>
                  {styles.length > 0 && (
                    <p className="mt-3 text-sm font-medium text-[#8A7357]">
                      {styles.length} style{styles.length > 1 ? "s" : ""} selected
                    </p>
                  )}
                </div>
              )}
            </motion.section>

            <div className="flex flex-col items-stretch gap-3 pt-2 sm:flex-row sm:justify-center">
              <button type="button" onClick={handleSubmit} className={artPrimaryBtnLg}>
                {hasExistingProfile ? "Save changes" : "Create my artist profile"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <CustomModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
      />
    </div>
  );
}

function DobCalendar({ value, onSelect, onClose }) {
  const initial = value ? new Date(value) : new Date();
  const [viewDate, setViewDate] = useState(
    Number.isNaN(initial.getTime()) ? new Date() : initial
  );

  const startOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const startDay = startOfMonth.getDay();
  const daysInMonth = new Date(
    viewDate.getFullYear(),
    viewDate.getMonth() + 1,
    0
  ).getDate();

  const weeks = [];
  let day = 1 - startDay;
  for (let w = 0; w < 6; w += 1) {
    const week = [];
    for (let d = 0; d < 7; d += 1) {
      week.push(day < 1 || day > daysInMonth ? null : day);
      day += 1;
    }
    weeks.push(week);
  }

  const monthLabel = viewDate.toLocaleString("default", { month: "short" });
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= currentYear - 80; y -= 1) years.push(y);

  return (
    <div className="text-xs text-[#2F251B]">
      <div className="mb-2 flex items-center justify-between gap-2">
        <button
          type="button"
          className="rounded-lg p-1 text-[#8A7357] transition hover:bg-[#FFF9EF]"
          onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
        >
          ‹
        </button>
        <div className="flex items-center gap-2">
          <div className="font-semibold text-[#5C4428]">{monthLabel}</div>
          <select
            className="rounded-lg border border-[#F5D99B] bg-[#FFFDF9] px-1 py-0.5 text-[11px] text-[#2F251B]"
            value={viewDate.getFullYear()}
            onChange={(e) => setViewDate(new Date(parseInt(e.target.value, 10), viewDate.getMonth(), 1))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="rounded-lg p-1 text-[#8A7357] transition hover:bg-[#FFF9EF]"
          onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
        >
          ›
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-1 text-[10px] text-[#9A7A55]">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d} className="text-center">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weeks.map((week, wi) =>
          week.map((dayNum, di) => {
            if (!dayNum) return <div key={`${wi}-${di}`} />;
            const isSelected =
              value &&
              (() => {
                const cur = new Date(value);
                return (
                  !Number.isNaN(cur.getTime()) &&
                  cur.getDate() === dayNum &&
                  cur.getMonth() === viewDate.getMonth() &&
                  cur.getFullYear() === viewDate.getFullYear()
                );
              })();
            return (
              <button
                type="button"
                key={`${wi}-${di}`}
                onClick={() => onSelect(new Date(viewDate.getFullYear(), viewDate.getMonth(), dayNum))}
                className={`h-8 w-8 rounded-full text-center text-xs leading-8 ${
                  isSelected
                    ? "bg-gradient-to-br from-[#F59E0B] to-[#F97316] font-semibold text-white"
                    : "text-[#2F251B] hover:bg-[#FFF9EF]"
                }`}
              >
                {dayNum}
              </button>
            );
          })
        )}
      </div>

      <div className="mt-2 flex justify-between">
        <button
          type="button"
          className="rounded-lg border border-[#F5D99B] px-2 py-1 text-[11px] font-medium text-[#5C4428] transition hover:bg-[#FFF9EF]"
          onClick={() => onSelect(new Date())}
        >
          Today
        </button>
        <button
          type="button"
          className="rounded-lg px-2 py-1 text-[11px] text-[#8A7357] transition hover:bg-[#FFF9EF]"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}
